import React, { useEffect, useRef, useState } from 'react';
import { Subject } from 'rxjs';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

import Notification from '../Notification';

import './index.scss';

// --- Global State for PushToast ---
const toastSubject = new Subject();
let toastId = 0;

export const pushToast = toast => {
  // Adds a unique ID to the toast before emitting
  toastSubject.next({ id: toastId++, ...toast });
};
// -----------------------------------

// 1. Destructure the 'toasts' prop from the component's arguments
const Toaster = ({ toasts = [] }) => {
  const [toastsList, updateToastsList] = useState([]);
  const ref = useRef();
  const timeout = 5000;

  // --- Shared Toast Management Functions ---

  // Adds a toast message to the state list
  const addToastMessage = toast => {
    updateToastsList(arr => [{ ...toast, show: true }, ...arr]);
  };

  // Sets a timeout to visually hide the toast (for CSS transitions)
  const hideToastMessage = (id, time = timeout) => {
    setTimeout(() => {
      updateToastsList(arr =>
        arr.map(val => (val.id === id ? { ...val, show: false } : val))
      );
    }, time);
  };

  // Sets a timeout to completely remove the toast from the DOM/state
  const removeToastMessage = id => {
    setTimeout(() => {
      updateToastsList(arr => arr.filter(val => val.id !== id));
    }, timeout);
  };

  // --- useEffects for Data Sources ---

  // 2. Handle toasts coming from the global RxJS subject (pushToast)
  useEffect(() => {
    const subscription = toastSubject.subscribe(toast => {
      addToastMessage(toast);
      hideToastMessage(toast.id); // Automatically hide after timeout
    });

    // Cleanup subscription on unmount
    return () => subscription.unsubscribe();
  }, []); // Empty dependency array means this runs once on mount

  // 3. Handle toasts coming from the component's 'toasts' prop
  useEffect(() => {
    if (toasts.length > 0) {
      // Process each toast in the array prop
      toasts.forEach(propToast => {
        // You MUST assign a unique ID and process it
        const newToast = { id: toastId++, ...propToast };
        addToastMessage(newToast);
        hideToastMessage(newToast.id); // Automatically hide
      });
    }
  }, [toasts]); // Reruns whenever the 'toasts' prop array changes

  // --- Render ---
  return (
    !!toastsList.length &&
    ReactDOM.createPortal(
      <ul className="toaster-holder" ref={ref}>
        {toastsList.map(notification => (
          <Notification
            key={notification.id}
            data={notification}
            onMessageHide={hideToastMessage}
            onClose={removeToastMessage} // The onClose here should likely call hideToastMessage first and then removeToastMessage later
          />
        ))}
      </ul>,
      document.body
    )
  );
};

Toaster.propTypes = {
  // Now explicitly expecting an array of objects for the prop
  toasts: PropTypes.arrayOf(PropTypes.object)
};

export default Toaster;
